/**
 * Connect > Catalog overrides at /connect/catalog.
 *
 * Manual overrides that shape what partners see in the portal catalog. Three
 * actions, explained inline: hide (remove an item partners would otherwise
 * see), feature (surface an item prominently), include (force an item in that
 * would otherwise be filtered out). List + add + delete.
 *
 * Note (judgment call): this lives on its own /connect/catalog page rather than
 * inside Pricing, because catalog visibility is a distinct concern from prices
 * and keeps each page focused.
 */

import { useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { useSession } from '@/lib/useSession';
import { formatDateShort } from '@/lib/dateUtils';
import {
  hasConnectPricing,
  isNotAvailable,
  useConnectCatalogOverrides,
  useUpsertCatalogOverride,
  useDeleteCatalogOverride,
  type CatalogOverride,
  type CatalogOverrideAction,
} from '@/lib/useConnect';
import {
  AccessDeniedCard,
  ConnectHeader,
  NotAvailableCard,
  Modal,
  inputCls,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
} from './connectShared';

const ACTION_TONES: Record<CatalogOverrideAction, string> = {
  hide: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  feature: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  include: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

const ACTION_EXPLAINED: Record<CatalogOverrideAction, string> = {
  hide: 'Hide removes an item from the partner catalog even if it would otherwise be shown.',
  feature: 'Feature surfaces an item prominently at the top of the partner catalog.',
  include: 'Include forces an item into the partner catalog even if it would otherwise be filtered out.',
};

export function ConnectCatalog() {
  const { user } = useSession();
  // Catalog overrides are a Pricing sub-page (judgment call: no dedicated
  // accessSlug of its own in the spec's slug table). No more read/write
  // levels: holding connect.pricing grants both.
  const canView = hasConnectPricing(user?.permissions, user?.superAdmin);
  const canWrite = canView;

  const overridesQ = useConnectCatalogOverrides();
  const del = useDeleteCatalogOverride();
  const [addOpen, setAddOpen] = useState(false);

  if (user && !canView) return <AccessDeniedCard />;
  const backendMissing = isNotAvailable(overridesQ.error);

  const remove = (o: CatalogOverride) => {
    if (window.confirm(`Remove the ${o.action} override on ${o.item_code}?`)) del.mutate(o.id);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ConnectHeader
        title="Catalog overrides"
        subtitle="Manual rules that shape what partners see in the portal catalog."
        actions={
          <>
            <ToolPinToggle toolSlug="connect-pricing" toolName="Pricing" />
            {canWrite && (
              <button type="button" onClick={() => setAddOpen(true)} className={secondaryBtnCls}>
                <Plus className="h-3 w-3" /> Add override
              </button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {(Object.keys(ACTION_EXPLAINED) as CatalogOverrideAction[]).map((a) => (
          <div key={a} className="rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
            <Pill tone={ACTION_TONES[a]}>{a}</Pill>
            <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{ACTION_EXPLAINED[a]}</p>
          </div>
        ))}
      </div>

      {backendMissing ? (
        <NotAvailableCard what="Catalog overrides" />
      ) : (
        <DataGridShell
          total={overridesQ.overrides.length}
          loaded={overridesQ.overrides.length}
          countNoun="overrides"
        >
          <table className="min-w-full text-sm">
            <thead className={gridTheadClassName}>
              <tr>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Brand</th>
                <th className="px-3 py-2 text-left font-medium">Item code</th>
                <th className="px-3 py-2 text-left font-medium w-[120px]">Action</th>
                <th className="px-3 py-2 text-left font-medium">Note</th>
                <th className="px-3 py-2 text-left font-medium w-[130px]">Added</th>
                <th className="px-3 py-2 text-right font-medium w-[80px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {overridesQ.isLoading ? (
                <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading overrides...</td></tr>
              ) : overridesQ.error ? (
                <tr><td colSpan={6} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {overridesQ.error.message}</td></tr>
              ) : overridesQ.overrides.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No catalog overrides yet.</td></tr>
              ) : (
                overridesQ.overrides.map((o) => (
                  <tr key={o.id} className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
                    <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{o.brand}</td>
                    <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">{o.item_code}</td>
                    <td className="px-3 py-2"><Pill tone={ACTION_TONES[o.action]}>{o.action}</Pill></td>
                    <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{o.note ?? '--'}</td>
                    <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(o.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      {canWrite && (
                        <button type="button" onClick={() => remove(o)} aria-label="Delete override" className="text-amp-ink-muted hover:text-red-700 dark:text-amp-dark-ink-muted"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DataGridShell>
      )}

      {addOpen && <AddOverrideModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function AddOverrideModal({ onClose }: { onClose: () => void }) {
  const upsert = useUpsertCatalogOverride();
  const [brand, setBrand] = useState('hammonds');
  const [itemCode, setItemCode] = useState('');
  const [action, setAction] = useState<CatalogOverrideAction>('hide');
  const [note, setNote] = useState('');

  const submit = () => {
    upsert.mutate(
      { brand, item_code: itemCode.trim(), action, note: note.trim() || undefined },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title="Add catalog override"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryBtnCls}>Cancel</button>
          <button type="button" onClick={submit} disabled={upsert.isPending || !itemCode.trim()} className={primaryBtnCls}>
            {upsert.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Add
          </button>
        </>
      }
    >
      {upsert.error && <p className="mb-3 text-xs text-red-700 dark:text-red-300">{isNotAvailable(upsert.error) ? 'Catalog overrides are not available yet.' : upsert.error.message}</p>}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Brand</label>
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className={inputCls}>
              <option value="hammonds">Hammond&apos;s</option>
              <option value="lolli_pops">Lolli &amp; Pops</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value as CatalogOverrideAction)} className={inputCls}>
              <option value="hide">Hide</option>
              <option value="feature">Feature</option>
              <option value="include">Include</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Item code</label>
          <input value={itemCode} onChange={(e) => setItemCode(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Note</label>
          <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} />
        </div>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{ACTION_EXPLAINED[action]}</p>
      </div>
    </Modal>
  );
}
